import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus } from 'lucide-react';
import { createSupplier, Customer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import { toast } from './toasts/SimpleToast';

interface AddSupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (supplier: Customer) => void;
}

export default function AddSupplierModal({
  isOpen,
  onClose,
  onSuccess
}: AddSupplierModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [supplierFormData, setSupplierFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setError(null);
      setSupplierFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      });
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleAddSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBusiness?.id) return;

    if (!supplierFormData.first_name.trim() || !supplierFormData.last_name.trim() || !supplierFormData.phone.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const supplier = await createSupplier({
        business_id: selectedBusiness.id,
        type: 'person',
        first_name: supplierFormData.first_name,
        last_name: supplierFormData.last_name,
        email: supplierFormData.email || null,
        phone: supplierFormData.phone || null,
        address: null,
        source: null,
        tags: [],
        last_interaction: null,
        lifetime_value: 0,
        next_service_date: null,
        referral_source: null,
        preferences: {},
        status: 'active',
        amount_spent: 0,
        credit_balance: 0,
        notes: null,
        tax_exempt: false,
        workflow_exempt: false,
        carfax_exempt: false,
        receive_reminders: false,
        receive_offers: false,
        is_supplier: true,
        repeat: false
      });

      toast.success({
        title: 'Supplier Added',
        description: 'Supplier has been created successfully.',
        duration: 3000
      });

      handleClose();
      onSuccess(supplier);
    } catch (err) {
      console.error('Error creating supplier:', err);
      setError(err instanceof Error ? err.message : 'Failed to create supplier');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return createPortal(
    <div
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100080,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div
        className={`bg-white transform transition-all duration-200 ${
          isMobile
            ? `fixed left-0 right-0 w-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[500px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100090,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(isMobile && { left: 0, right: 0 }),
          ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden relative">
          {/* Header */}
          <div className={`bg-gray-50 border-b border-gray-200 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} flex items-center justify-between sticky top-0 z-10`}>
            <h2 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900`}>Add Supplier</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form
            onSubmit={handleAddSupplier}
            className={`flex-1 overflow-y-auto overflow-x-hidden min-w-0 bg-white ${isMobile ? 'px-4 py-4' : 'px-6 py-6'}`}
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  value={supplierFormData.first_name}
                  onChange={(e) => setSupplierFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  className={`w-full px-4 ${isMobile ? 'py-3 text-base' : 'py-3 text-sm'} bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Enter first name"
                  style={isMobile ? { fontSize: '16px' } : undefined}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={supplierFormData.last_name}
                  onChange={(e) => setSupplierFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  className={`w-full px-4 ${isMobile ? 'py-3 text-base' : 'py-3 text-sm'} bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Enter last name"
                  style={isMobile ? { fontSize: '16px' } : undefined}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={supplierFormData.email}
                  onChange={(e) => setSupplierFormData(prev => ({ ...prev, email: e.target.value }))}
                  className={`w-full px-4 ${isMobile ? 'py-3 text-base' : 'py-3 text-sm'} bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Enter email address"
                  style={isMobile ? { fontSize: '16px' } : undefined}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  value={supplierFormData.phone}
                  onChange={(e) => setSupplierFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className={`w-full px-4 ${isMobile ? 'py-3 text-base' : 'py-3 text-sm'} bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Enter phone number"
                  style={isMobile ? { fontSize: '16px' } : undefined}
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}
            </div>
          </form>

          {/* Footer */}
          <div className={`sticky bottom-0 bg-white border-t border-gray-200 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} ${isMobile ? 'flex-col gap-2' : 'flex items-center justify-end gap-3'}`}>
            {isMobile && (
              <button
                onClick={handleAddSupplier as any}
                disabled={loading}
                className="w-full px-4 py-3.5 text-base bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 flex items-center justify-center gap-2 font-medium touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? 'Adding...' : 'Add Supplier'}
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className={`${isMobile ? 'w-full px-4 py-3 text-base' : 'px-4 py-2'} text-gray-700 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors touch-manipulation`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Cancel
            </button>
            {!isMobile && (
              <button
                onClick={handleAddSupplier as any}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 flex items-center gap-2 font-medium touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? 'Adding...' : 'Add Supplier'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}